// src/components/OBLTeamScreen.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../firebase";

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";

import "./OBLTeamScreen.css";

const TOPICS = [
  "Drugs",
  "Human Trafficking",
  "Immigration Issues",
  "Weapons",
  "Cartel Related",
  "Report a Tip",
];

// Basic helper for “contains” search without indexes
function includesText(value, q) {
  if (!value) return false;
  return String(value).toLowerCase().includes(q.toLowerCase());
}

function OBLTeamScreen() {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();

  // Tabs: Journalist / Friends / Topic (matches the PDF)
  const [activeTab, setActiveTab] = useState("journalist"); // journalist | friends | topic

  // Search + filters
  const [searchText, setSearchText] = useState("");
  const [selectedTopic, setSelectedTopic] = useState(TOPICS[0]);
  const [sortMode, setSortMode] = useState("recent"); // recent | relevance (simple)

  // Data
  const [journalists, setJournalists] = useState([]);
  const [friends, setFriends] = useState([]);
  const [topicPosts, setTopicPosts] = useState([]);

  // Saved “OBL Team” items (favorites)
  const [savedMap, setSavedMap] = useState({}); // key => team_members doc id
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Guard
  useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  // Load current user’s saved items
  useEffect(() => {
    async function loadSaved() {
      if (!user) return;
      setErr("");
      try {
        const qSaved = query(
          collection(db, "team_members"),
          where("ownerId", "==", user.uid)
        );
        const snap = await getDocs(qSaved);

        const map = {};
        snap.forEach((d) => {
          const data = d.data();
          const key = `${data.memberType}:${data.memberId}`;
          map[key] = d.id;
        });
        setSavedMap(map);
      } catch (e) {
        console.error(e);
        setErr("Failed to load your OBL Team list.");
      }
    }
    loadSaved();
  }, [user]);

  // Fetch lists (simple approach: fetch a capped set, then client-filter/search)
  useEffect(() => {
    async function fetchTabData() {
      if (!user) return;

      setLoading(true);
      setErr("");

      try {
        if (activeTab === "journalist") {
          // Load journalists
          const qUsers = query(
            collection(db, "users"),
            where("role", "==", "journalist"),
            orderBy("displayName"),
            limit(200)
          );
          const snap = await getDocs(qUsers);
          const rows = [];
          snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
          setJournalists(rows);
        }

        if (activeTab === "friends") {
          // Load non-journalist users (or everyone except you)
          const qUsers = query(collection(db, "users"), orderBy("displayName"), limit(200));
          const snap = await getDocs(qUsers);
          const rows = [];
          snap.forEach((d) => {
            if (d.id !== user.uid) rows.push({ id: d.id, ...d.data() });
          });
          setFriends(rows);
        }

        if (activeTab === "topic") {
          // Load approved posts and filter by topic tag client-side (simpler)
          // If you have `topicTags` as an array field, you can use:
          // where("topicTags", "array-contains", selectedTopic)
          const qPosts = query(
            collection(db, "posts"),
            where("approved", "==", true),
            orderBy("createdAt", "desc"),
            limit(100)
          );
          const snap = await getDocs(qPosts);
          const rows = [];
          snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
          setTopicPosts(rows);
        }
      } catch (e) {
        console.error(e);
        setErr("Failed to load data. Check Firestore rules/indexes.");
      } finally {
        setLoading(false);
      }
    }

    fetchTabData();
  }, [activeTab, user, selectedTopic]);

  // Save / Unsave
  const isSaved = (memberType, memberId) => {
    const key = `${memberType}:${memberId}`;
    return Boolean(savedMap[key]);
  };

  const handleToggleSave = async (memberType, memberId) => {
    if (!user) return;

    const key = `${memberType}:${memberId}`;
    const existingDocId = savedMap[key];

    try {
      if (existingDocId) {
        await deleteDoc(doc(db, "team_members", existingDocId));
        setSavedMap((prev) => {
          const copy = { ...prev };
          delete copy[key];
          return copy;
        });
      } else {
        const ref = await addDoc(collection(db, "team_members"), {
          ownerId: user.uid,
          memberType, // "journalist" | "friend" | "topicPost"
          memberId,
          createdAt: serverTimestamp(),
        });
        setSavedMap((prev) => ({ ...prev, [key]: ref.id }));
      }
    } catch (e) {
      console.error(e);
      setErr("Could not update your OBL Team list. Try again.");
    }
  };

  // Client-side filtered lists
  const filteredJournalists = useMemo(() => {
    const q = searchText.trim();
    const base = journalists.filter((u) => {
      if (!q) return true;
      return (
        includesText(u.displayName, q) ||
        includesText(u.email, q) ||
        includesText(u.username, q)
      );
    });

    return base;
  }, [journalists, searchText]);

  const filteredFriends = useMemo(() => {
    const q = searchText.trim();
    const base = friends.filter((u) => {
      if (!q) return true;
      return (
        includesText(u.displayName, q) ||
        includesText(u.email, q) ||
        includesText(u.username, q)
      );
    });

    return base;
  }, [friends, searchText]);

  const filteredTopicPosts = useMemo(() => {
    const q = searchText.trim();

    let base = topicPosts;

    // Filter by topic if stored in tags
    base = base.filter((p) => {
      const tags = Array.isArray(p.topicTags) ? p.topicTags : [];
      return tags.map(String).some((t) => t.toLowerCase() === selectedTopic.toLowerCase());
    });

    // Search
    if (q) {
      base = base.filter(
        (p) => includesText(p.title, q) || includesText(p.description, q) || includesText(p.location, q)
      );
    }

    // Sort
    if (sortMode === "relevance" && q) {
      // crude relevance: title hit first
      base = [...base].sort((a, b) => {
        const aScore = (includesText(a.title, q) ? 2 : 0) + (includesText(a.description, q) ? 1 : 0);
        const bScore = (includesText(b.title, q) ? 2 : 0) + (includesText(b.description, q) ? 1 : 0);
        return bScore - aScore;
      });
    }

    return base;
  }, [topicPosts, selectedTopic, searchText, sortMode]);

  return (
    <div className="obl-team-container">
      <div className="obl-team-header">
        <h1>OBL Team</h1>

        <div className="obl-team-tabs">
          <button
            className={activeTab === "journalist" ? "tab active" : "tab"}
            onClick={() => setActiveTab("journalist")}
          >
            Journalist
          </button>
          <button
            className={activeTab === "friends" ? "tab active" : "tab"}
            onClick={() => setActiveTab("friends")}
          >
            OBL Friends
          </button>
          <button
            className={activeTab === "topic" ? "tab active" : "tab"}
            onClick={() => setActiveTab("topic")}
          >
            Topic
          </button>
        </div>

        <div className="obl-team-controls">
          <input
            type="text"
            placeholder="Search by name, username, email, location…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />

          {activeTab === "topic" && (
            <>
              <select value={selectedTopic} onChange={(e) => setSelectedTopic(e.target.value)}>
                {TOPICS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>

              <select value={sortMode} onChange={(e) => setSortMode(e.target.value)}>
                <option value="recent">Most Recent</option>
                <option value="relevance">Relevance</option>
              </select>
            </>
          )}
        </div>

        {err && <p className="error-text">{err}</p>}
        {loading && <p className="muted-text">Loading…</p>}
      </div>

      {/* LISTS */}
      <div className="obl-team-list">
        {activeTab === "journalist" && (
          <>
            {filteredJournalists.length === 0 ? (
              <p className="muted-text">No journalists found.</p>
            ) : (
              filteredJournalists.map((u) => (
                <div key={u.id} className="card-row">
                  <div className="left">
                    <img
                      className="avatar"
                      src={u.photoURL || "/avatar-placeholder.png"}
                      alt="avatar"
                    />
                    <div className="meta">
                      <div className="title">{u.displayName || u.username || "Unnamed"}</div>
                      <div className="sub">{u.email || ""}</div>
                    </div>
                  </div>

                  <div className="right">
                    <button
                      className={isSaved("journalist", u.id) ? "btn saved" : "btn"}
                      onClick={() => handleToggleSave("journalist", u.id)}
                    >
                      {isSaved("journalist", u.id) ? "Added ✓" : "Add to OBL Team"}
                    </button>

                    <button className="btn ghost" onClick={() => navigate(`/profile/${u.id}`)}>
                      View
                    </button>
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {activeTab === "friends" && (
          <>
            {filteredFriends.length === 0 ? (
              <p className="muted-text">No friends found.</p>
            ) : (
              filteredFriends.map((u) => (
                <div key={u.id} className="card-row">
                  <div className="left">
                    <img
                      className="avatar"
                      src={u.photoURL || "/avatar-placeholder.png"}
                      alt="avatar"
                    />
                    <div className="meta">
                      <div className="title">{u.displayName || u.username || "Unnamed"}</div>
                      <div className="sub">{u.email || ""}</div>
                    </div>
                  </div>

                  <div className="right">
                    <button
                      className={isSaved("friend", u.id) ? "btn saved" : "btn"}
                      onClick={() => handleToggleSave("friend", u.id)}
                    >
                      {isSaved("friend", u.id) ? "Added ✓" : "Add to OBL Team"}
                    </button>

                    <button className="btn ghost" onClick={() => navigate(`/profile/${u.id}`)}>
                      View
                    </button>
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {activeTab === "topic" && (
          <>
            {filteredTopicPosts.length === 0 ? (
              <p className="muted-text">No posts found for this topic.</p>
            ) : (
              filteredTopicPosts.map((p) => (
                <div key={p.id} className="card-row">
                  <div className="left">
                    <div className="meta">
                      <div className="title">{p.title || "Untitled"}</div>
                      <div className="sub">
                        {p.location || "Unknown location"} •{" "}
                        {Array.isArray(p.topicTags) ? p.topicTags.join(", ") : ""}
                      </div>
                      <div className="desc">{p.description || ""}</div>
                    </div>
                  </div>

                  <div className="right">
                    <button
                      className={isSaved("topicPost", p.id) ? "btn saved" : "btn"}
                      onClick={() => handleToggleSave("topicPost", p.id)}
                    >
                      {isSaved("topicPost", p.id) ? "Saved ✓" : "Save"}
                    </button>

                    <button className="btn ghost" onClick={() => navigate(`/post/${p.id}`)}>
                      Open
                    </button>
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>

      <div className="obl-team-footer">
        <button className="btn primary" onClick={() => navigate("/submit")}>
          Submit News
        </button>
      </div>
    </div>
  );
}

export default OBLTeamScreen;
